import React, { useContext, useMemo, useState } from "react";

import { initialState, RCACorporateDashboardContext } from "./RCACorporateDashboardContext";
import {
    IRCACorporateDashboardContextProps,
    IRCACorporateDashboardFiltersSelected,
} from "./RCACorporateDashboardContextInterfaces";
import { UsersEntity } from "entities/Users/UsersEntity";
import { IGetUserFiltersPreferencesQuery } from "api/queries/usersAPI";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import { FiltersPreferencesReportsEnum } from "constants/constants";
import { useTranslation } from "react-i18next";
import { filterObjectProperties, objectComparator } from "utilities/utilities";
import { Alert, Snackbar } from "@mui/material";

export const valiRCACorporateDashboardFiltersDefaultFields = [
    "periodOption",
    "typeOption",
    "divisionIds",
    "totalDivisions",
    "regionIds",
    "totalRegions",
];

export const RCACorporateDashboardContextProvider: React.FC<IRCACorporateDashboardContextProps> = ({ children }) => {
    const { t } = useTranslation();
    const { currentUser } = useContext(CorporateContext);

    const [filters, setFilters] = useState<IRCACorporateDashboardFiltersSelected>({
        periodOption: initialState.filters.periodOption,
        typeOption: initialState.filters.typeOption,
        complianceType: initialState.filters.complianceType,
        divisionIds: initialState.filters.divisionIds,
        totalDivisions: initialState.filters.totalDivisions,
        regionIds: initialState.filters.regionIds,
        totalRegions: initialState.filters.totalRegions,
    });
    const [userFiltersDefault, setUserFiltersDefault] = useState<IRCACorporateDashboardFiltersSelected | null>(null);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const { upsertOperation, loading: loadingUpsertDefault } = UsersEntity.UpsertUserFiltersDefaultPreference();
    const { removeOperation, loading: loadingRemoveDefault } = UsersEntity.RemoveUserFiltersDefaultPreference();

    const handleQueryCompletion = (data: IGetUserFiltersPreferencesQuery) => {
        const userFilterPreference = data.user_filter_preferences;
        if (userFilterPreference[0]) {
            const filtersDefault = JSON.parse(
                userFilterPreference[0].filter_selection,
            ) as IRCACorporateDashboardFiltersSelected;
            setUserFiltersDefault(filtersDefault);
            setFilters((prev) => ({ ...prev, ...filtersDefault }));
        } else {
            setUserFiltersDefault(null);
        }
    };
    const { loading: loadingGetDefault, refetch } = UsersEntity.GetUserFiltersPreferences(
        handleQueryCompletion,
        FiltersPreferencesReportsEnum.RCA_CORPORATE_DASHBOARD,
        currentUser?.user_id,
    );

    const handleSetFiltersDefault = async (filters: IRCACorporateDashboardFiltersSelected) => {
        try {
            const validFilters = filterObjectProperties(filters, valiRCACorporateDashboardFiltersDefaultFields);
            await upsertOperation({
                variables: {
                    userPreference: {
                        user_id: currentUser?.user_id,
                        report_id: FiltersPreferencesReportsEnum.RCA_CORPORATE_DASHBOARD,
                        filter_selection: JSON.stringify(validFilters),
                    },
                },
            });
            await refetch();
            setSuccessMessage(t("locations.mill_dashboards.filters_saved"));
        } catch (error) {
            console.error(error);
            setErrorMessage(t("locations.mill_dashboards.filters_error"));
        }
    };

    const handleRemoveDefaultFilters = async () => {
        try {
            await removeOperation({
                variables: {
                    userId: currentUser?.user_id,
                    reportId: FiltersPreferencesReportsEnum.RCA_CORPORATE_DASHBOARD,
                },
            });
            setUserFiltersDefault(null);
            setSuccessMessage(t("locations.mill_dashboards.filters_removed"));
        } catch (error) {
            console.error(error);
            setErrorMessage(t("locations.mill_dashboards.filters_error"));
        }
    };

    const handleSetFilters = (value: number) => {
        setFilters((prev) => ({ ...prev, periodOption: value }));
    };

    const handleSetTypeFilter = (value: string) => {
        setFilters((prev) => ({ ...prev, typeOption: value }));
    };

    const handleSetComplianceType = (value: string) => {
        setFilters((prev) => ({ ...prev, complianceType: value }));
    };

    const handleSetDivisions = (value: (number | string)[], total?: number, isAutoInit?: boolean) => {
        setFilters((prev) => {
            if (isAutoInit && prev.divisionIds.length > 0) return prev;
            return { ...prev, divisionIds: value, ...(total ? { totalDivisions: total } : {}) };
        });
    };

    const handleSetRegions = (value: (number | string)[], total?: number, isAutoInit?: boolean) => {
        setFilters((prev) => {
            if (isAutoInit && prev.regionIds.length > 0) return prev;
            return { ...prev, regionIds: value, ...(total ? { totalRegions: total } : {}) };
        });
    };

    const isFiltersDefault = useMemo(() => {
        if (userFiltersDefault) {
            return objectComparator(userFiltersDefault, filters, valiRCACorporateDashboardFiltersDefaultFields);
        }
        return false;
    }, [userFiltersDefault, filters]);

    const providerValue = {
        userFiltersDefault,
        filters,
        handleSetFilters,
        handleSetTypeFilter,
        handleSetComplianceType,
        handleSetDivisions,
        handleSetRegions,
        handleSetFiltersDefault,
        handleRemoveDefaultFilters,
        loadingDefaultOperations: loadingGetDefault || loadingRemoveDefault || loadingUpsertDefault,
        isFiltersDefault,
    };

    return (
        <RCACorporateDashboardContext.Provider value={providerValue}>
            <Snackbar
                open={!!successMessage || !!errorMessage}
                autoHideDuration={2000}
                onClose={() => {
                    setErrorMessage("");
                    setSuccessMessage("");
                }}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Alert severity={successMessage ? "success" : "error"} sx={{ width: "100%" }}>
                    <span style={{ whiteSpace: "pre-line" }}>{successMessage || errorMessage}</span>
                </Alert>
            </Snackbar>
            {children}
        </RCACorporateDashboardContext.Provider>
    );
};
